import { KurentoClient } from 'kurento-client';
import { isCameraStreamLogsEnabled } from 'services/CameraStream/config';
import { axiosAiq } from 'config';
import { getLocalNetworkAccessState, subscribeLocalNetworkAccess, isLocalNetworkUrl } from 'utils/localNetworkAccess';
import { getPermissionState, subscribePermission } from 'utils/browserPermissions';
import { store } from 'store/store';
import { streamPermissionsActions } from 'store/streamPermissionsSlice/streamPermissionsSlice';

const MAX_WS_CONNECTION_ATTEMPTS = window.MAX_KURENTO_WS_FAILURES || 3;
const MAX_RECOVERY_ATTEMPTS = 3;
const RECOVERY_BASE_DELAY_MS = 4000;

//TODO split into multiple classes
class KurentoClientManager {
    #clients = new Map();
    #clientCreationPromises = new Map();
    #recoveryAttempts = new Map();
    #fetchingKurentoConfigPromise = null;
    #handleWsExhaustedEvent = (event) => this.#handleWsExhausted(event);
    #lnaWatchUnsubscribe = null;
    #micWatchUnsubscribe = null;
    #localNetworkPromptTriggered = false;
    #micPromptTriggered = false;

    constructor() {
        window.addEventListener('kurento-ws-exhausted', this.#handleWsExhaustedEvent);
    }

    async get(kurentoId) {
        await this.#ensureKurentoRegistered(kurentoId);
        if (!this.#clients.get(kurentoId)?.client) {
            this.#assertRecoveryNotExhausted(kurentoId);
            if (!this.#clientCreationPromises.has(kurentoId)) this.#create(kurentoId);
            await this.#clientCreationPromises.get(kurentoId);
            this.#log(`get(): Awaited for KurentoClient id=${kurentoId}`);
        }
        const kurentoClient = this.#clients.get(kurentoId);
        if (!kurentoClient?.active) throw new Error(`Kurento id=${kurentoId} is inactive!`, { cause: `Kurento id=${kurentoId} is inactive!` });
        if (!kurentoClient?.client)
            throw new Error(`Kurento id=${kurentoId} error getting client`, { cause: `Error geting kurento id=${kurentoId} client!` });

        this.#log(`get(): Returning KurentoClient id=${kurentoId}`);
        return kurentoClient.client;
    }

    async releaseMediaObject(kurentoId, mediaObjectId) {
        const client = await this.get(kurentoId);
        const mediaObject = await client.getMediaobjectById(mediaObjectId);
        await mediaObject.release();
        this.#log(`releaseMediaObject(): Released mediaObjectId=${mediaObjectId} on kurentoId=${kurentoId}`);
    }

    isClientCreated(kurentoId) {
        return !!this.#clients.get(kurentoId)?.client;
    }

    isKurentoActive(kurentoId) {
        return !!this.#clients.get(kurentoId)?.active;
    }

    registerKurento(kurentoId, wsUri, active) {
        this.#log(`registerKurento(): id=${kurentoId}, wsUri=${wsUri}, active=${active}`);
        this.#clients.set(kurentoId, { client: null, id: kurentoId, wsUri, active });
    }

    updateKurentoActiveState(kurentoId, active) {
        this.#log(`updateKurentoActiveState(): id=${kurentoId}, active=${active}`);
        const kurentoClientObj = this.#clients.get(kurentoId);
        if (!kurentoClientObj) {
            this.#log(`updateKurentoActiveState(): KurentoClient id=${kurentoId} not found`);
            return;
        }
        kurentoClientObj.active = active;
        if (!active) this.#disposeClient(kurentoClientObj);
        else this.#recoveryAttempts.delete(kurentoId);
    }

    // ─── Client Lifecycle ─────────────────────────────────────────────

    #create(kurentoId) {
        if (this.#clientCreationPromises.has(kurentoId)) {
            this.#log(`#create(): creation already in-flight for id=${kurentoId}, skipping`);
            return;
        }
        this.#log(`#create(): KurentoClient id=${kurentoId} initiating creation`);
        const kurentoClientObj = this.#clients.get(kurentoId);
        this.#handleKurentoClientCreationPromise(this.#kurentoClientCreationPromiseFactory(kurentoClientObj));
    }

    #handleKurentoClientCreationPromise({ kurentoClientPromise, kurentoClientObj }) {
        if (!kurentoClientObj) {
            this.#log(`#handleKurentoClientCreationPromise(): Kurento client object missing`, 'error');
            return;
        }

        const kurentoId = kurentoClientObj.id;
        kurentoClientPromise
            .then((client) => {
                this.#log(`KurentoClient id=${kurentoId} created`);
                kurentoClientObj.client = client;
                this.#recoveryAttempts.delete(kurentoId);
            })
            .catch((err) => {
                this.#log(`Error creating KurentoClient, id=${kurentoId} wsUri=${kurentoClientObj.wsUri} message=${err?.message}`, 'error');
            })
            .finally(() => {
                this.#clientCreationPromises.delete(kurentoId);
            });
    }

    #kurentoClientCreationPromiseFactory(kurentoClientObj) {
        const kurentoId = kurentoClientObj?.id;
        const wsUri = kurentoClientObj?.wsUri;

        const kurentoClientPromise = (async () => {
            if (!kurentoClientObj) {
                this.#log(`#create(): KurentoClient id=${kurentoId} creation failed! Kurento Client Object not found`);
                const message = `Kurento Client Object not found!`;
                throw new Error(message, { cause: message });
            }
            if (!this.isKurentoActive(kurentoId)) {
                this.#log(`#create(): KurentoClient id=${kurentoId} creation failed! Kurento is inactive`);
                const message = `Kurento id=${kurentoId} is inactive!`;
                throw new Error(message, { cause: message });
            }

            await this.#assertStreamPermissions(kurentoId, wsUri);

            this.#log(`#create(): KurentoClient id=${kurentoId} connecting to wsUri=${wsUri} (failAfter=${MAX_WS_CONNECTION_ATTEMPTS})`);

            return new Promise((res, rej) => {
                KurentoClient(wsUri, { failAfter: MAX_WS_CONNECTION_ATTEMPTS }, (err, client) => {
                    if (err) {
                        this.#log(
                            `#create(): KurentoClient id=${kurentoId} WS connection to wsUri=${wsUri} failed, error=${err?.message || err}`,
                            'error'
                        );
                        rej(
                            new Error(`KurentoClient id=${kurentoId} creation failed!`, {
                                cause: `KurentoClient id=${kurentoId} creation failed!`,
                            })
                        );
                    } else res(client);
                });
            });
        })();

        this.#clientCreationPromises.set(kurentoId, kurentoClientPromise);

        return { kurentoClientPromise, kurentoClientObj };
    }

    async #assertStreamPermissions(kurentoId, wsUri) {
        // Evaluate BOTH permissions before failing, so the dialog reports every missing one at once
        // rather than only the first. Each evaluation flags/clears its own dialog entry and never
        // throws; the orchestrator throws once if anything required was denied.
        const micState = await this.#evaluateMicPermission(kurentoId);
        const localNetworkState = await this.#evaluateLocalNetworkPermission(kurentoId, wsUri);

        const blocked = [];
        if (micState === 'denied') blocked.push('microphone');
        if (localNetworkState === 'denied') blocked.push('local network access');
        if (blocked.length) {
            const message = `Required permission(s) not granted for kurento id=${kurentoId}: ${blocked.join(', ')}`;
            throw new Error(message, { cause: message });
        }
    }

    // Detection-first: read the permission via the Permissions API instead of blocking on getUserMedia.
    // Returns 'granted' | 'prompt' | 'denied' | 'unknown'; flags/clears the mic dialog entry.
    async #evaluateMicPermission(kurentoId) {
        this.#watchMicPermission();

        const state = await this.#getMicPermissionState();
        this.#log(`#evaluateMicPermission(): initial microphone permission state=${state} for id=${kurentoId}`, state === 'denied' ? 'error' : 'log');

        if (state === 'denied') {
            store.dispatch(streamPermissionsActions.reportMicDenied());
            return 'denied';
        }
        store.dispatch(streamPermissionsActions.clearMicDenied());
        if (state === 'prompt' && !this.#micPromptTriggered) {
            this.#micPromptTriggered = true;
            this.#promptForMic();
        }
        return state;
    }

    // Returns 'granted' | 'prompt' | 'denied' | 'unknown'; flags/clears the local-network dialog entry.
    async #evaluateLocalNetworkPermission(kurentoId, wsUri) {
        if (isLocalNetworkUrl(window.location.origin)) {
            // App served from a local / LAN origin, so reaching Kurento is a local -> local request,
            // which Chrome never gates on LNA - skip (no public -> local boundary exists to prompt on).
            this.#log(
                `#evaluateLocalNetworkPermission(): SKIP id=${kurentoId} - app origin ${window.location.origin} is local, so ` +
                    `local->local is not LNA-gated`
            );
            store.dispatch(streamPermissionsActions.clearLocalNetworkDenied());
            return 'granted';
        }

        this.#watchLocalNetworkPermission();

        const state = await getLocalNetworkAccessState();
        this.#log(
            `#evaluateLocalNetworkPermission(): initial local network access state=${state} for id=${kurentoId} wsUri=${wsUri}`,
            state === 'denied' ? 'error' : 'log'
        );

        if (state === 'denied') {
            // Explicitly denied: Chrome will not re-prompt and the connection fails. Show the guidance
            // dialog (enable local network in site settings + reload) and let the caller block.
            store.dispatch(streamPermissionsActions.reportLocalNetworkDenied());
            return 'denied';
        }
        // 'granted' | 'unknown' | 'prompt': not denied, so never block or show the dialog. On 'prompt'
        // the caller lets the connection proceed and Chrome raises its own LNA prompt on the real
        // request; we also fire a supplementary probe (once) to surface it a little sooner.
        store.dispatch(streamPermissionsActions.clearLocalNetworkDenied());
        if (state === 'prompt' && !this.#localNetworkPromptTriggered) {
            this.#localNetworkPromptTriggered = true;
            this.#promptForLocalNetwork(wsUri);
        }
        return state;
    }

    // Trigger Chrome's Local Network Access prompt by probing the Kurento host over HTTP(S). WebSockets
    // are not (yet) gated on LNA, so we must issue a fetch: a request to a private-IP / .local host
    // raises the prompt, and targetAddressSpace also covers hostnames that resolve to a private IP.
    // Non-blocking and best-effort - the probe itself is expected to fail (no HTTP endpoint / CORS);
    // we only care that it surfaces the prompt, after which the watcher reconciles the dialog.
    #promptForLocalNetwork(wsUri) {
        let probeUrl;
        try {
            // Match the wsUri scheme: wss:// -> https:// (a DNS-name Kurento has a valid cert, and https
            // avoids the mixed-content block a public hostname over http would hit); ws:// -> http://
            // (a bare private-IP literal is mixed-content exempt and has no usable cert for https).
            const url = new URL(wsUri);
            const scheme = url.protocol === 'wss:' ? 'https' : 'http';
            probeUrl = `${scheme}://${url.host}/`;
        } catch {
            this.#log(`#promptForLocalNetwork(): could not derive probe URL from wsUri=${wsUri}`, 'log');
            return;
        }
        this.#log(`#promptForLocalNetwork(): probing ${probeUrl} to raise the LNA prompt`);

        // Plain (CORS-mode) fetch, matching Chrome's documented trigger - no-cors bypasses the LNA
        // preflight. targetAddressSpace declares intent for names that resolve to a private IP. The
        // request is expected to fail (no HTTP endpoint / CORS); we only need it to raise the prompt,
        // and the watcher reports the user's answer. Abort after 5s so a TLS-only port can't hang it.
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 5000);
        try {
            fetch(probeUrl, { targetAddressSpace: 'local', signal: controller.signal })
                .then(() => this.#log(`#promptForLocalNetwork(): probe to ${probeUrl} resolved (local network reachable)`))
                .catch((err) => this.#log(`#promptForLocalNetwork(): probe to ${probeUrl} rejected: ${err?.name || err?.message || err}`, 'log'))
                .finally(() => clearTimeout(timer));
        } catch (err) {
            clearTimeout(timer);
            this.#log(`#promptForLocalNetwork(): probe fetch could not start for ${probeUrl}: ${err?.message}`, 'log');
        }
    }

    // Only a hard 'denied' shows the dialog; 'granted' and 'prompt' clear it, matching
    // #evaluateLocalNetworkPermission (we don't block or nag on 'prompt' - Chrome's own prompt drives
    // it, so a change into 'prompt' must not open the dialog).
    #watchLocalNetworkPermission() {
        if (this.#lnaWatchUnsubscribe) return;
        this.#lnaWatchUnsubscribe = subscribeLocalNetworkAccess((state) => {
            this.#log(`#watchLocalNetworkPermission(): local network access permission changed to ${state}`, state === 'denied' ? 'error' : 'log');
            if (state === 'denied') store.dispatch(streamPermissionsActions.reportLocalNetworkDenied());
            else store.dispatch(streamPermissionsActions.clearLocalNetworkDenied());
        });
    }

    // Only a hard 'denied' shows the dialog; 'granted' and 'prompt' clear it, matching
    // #evaluateMicPermission (we don't block or nag on 'prompt' - getUserMedia's own prompt drives it,
    // so a change into 'prompt' must not open the dialog).
    #watchMicPermission() {
        if (this.#micWatchUnsubscribe) return;
        this.#micWatchUnsubscribe = subscribePermission('microphone', (state) => {
            this.#log(`#watchMicPermission(): microphone permission changed to ${state}`, state === 'denied' ? 'error' : 'log');
            if (state === 'denied') store.dispatch(streamPermissionsActions.reportMicDenied());
            else store.dispatch(streamPermissionsActions.clearMicDenied());
        });
    }

    #disposeClient(kurentoClientObj) {
        const kurentoId = kurentoClientObj.id;
        try {
            const client = kurentoClientObj.client;
            kurentoClientObj.client = null;
            client?.close();
            this.#log(`#disposeClient(): KurentoClient id=${kurentoId} disposed`);
        } catch (err) {
            this.#log(`#disposeClient(): Error disposing KurentoClient id=${kurentoId}: ${err?.message}`, 'error');
        }
    }

    // ─── WS Connection Exhaustion Recovery ─────────────
    #handleWsExhausted(event) {
        const wsUri = event.detail?.wsUri;
        const kurentoClientObj = this.#findClientByWsUri(wsUri);
        if (!kurentoClientObj) {
            this.#log(`#handleWsExhausted(): No registered client for wsUri=${wsUri}`, 'warn');
            return;
        }
        const kurentoId = kurentoClientObj.id;
        const recoveryCount = (this.#recoveryAttempts.get(kurentoId) || 0) + 1;
        this.#recoveryAttempts.set(kurentoId, recoveryCount);
        this.#log(
            `#handleWsExhausted(): KurentoClient id=${kurentoId} wsUri=${wsUri} exhausted after ${MAX_WS_CONNECTION_ATTEMPTS} attempts (recovery ${recoveryCount}/${MAX_RECOVERY_ATTEMPTS})`,
            'error'
        );
        this.#disposeClient(kurentoClientObj);
        if (recoveryCount > MAX_RECOVERY_ATTEMPTS) {
            this.#log(`#handleWsExhausted(): KurentoClient id=${kurentoId} exceeded max recovery attempts, giving up`, 'error');
            return;
        }
        this.#recoverClient(kurentoId);
    }

    async #recoverClient(kurentoId) {
        try {
            this.#log(`#recoverClient(): Fetching fresh config for id=${kurentoId}`);
            const configs = await this.#fetchKurentoConfig();

            const config = configs.find((c) => c.id === kurentoId) || null;
            if (!config) {
                this.#log(`#recoverClient(): No config found for id=${kurentoId}`, 'warn');
                return;
            }
            const clientObj = this.#clients.get(kurentoId);
            if (!clientObj) return;
            this.#applyConfigToClient(clientObj, config);

            if (!clientObj.active) {
                this.#log(`#recoverClient(): id=${kurentoId} is inactive, skipping reconnect`);
                return;
            }

            if (!clientObj.wsUri) {
                this.#log(`#recoverClient(): id=${kurentoId} has empty wsUri, skipping reconnect`, 'warn');
                return;
            }

            const recoveryCount = this.#recoveryAttempts.get(kurentoId) || 1;
            const delayMs = RECOVERY_BASE_DELAY_MS * Math.pow(2, recoveryCount - 1);

            this.#log(`#recoverClient(): id=${kurentoId} is active, reconnecting to wsUri=${clientObj.wsUri} after ${delayMs}ms delay`);
            await this.#delay(delayMs);
            this.#create(kurentoId);
        } catch (err) {
            this.#log(`#recoverClient(): Error recovering id=${kurentoId}: ${err?.message}`, 'error');
        }
    }

    // ─── Helpers ──

    #delay(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    #findClientByWsUri(wsUri) {
        for (const [, clientObj] of this.#clients) {
            if (clientObj.wsUri === wsUri) return clientObj;
        }
        return null;
    }

    #assertRecoveryNotExhausted(kurentoId) {
        const exhausted = (this.#recoveryAttempts.get(kurentoId) || 0) > MAX_RECOVERY_ATTEMPTS;
        const inFlight = this.#clientCreationPromises.has(kurentoId);
        if (exhausted && !inFlight) {
            throw new Error(`Kurento id=${kurentoId} recovery exhausted`, {
                cause: `Kurento id=${kurentoId} recovery exhausted after ${MAX_RECOVERY_ATTEMPTS} attempt cycles`,
            });
        }
    }

    async #ensureKurentoRegistered(kurentoId) {
        if (this.#clients.has(kurentoId)) return;
        if (this.#fetchingKurentoConfigPromise) return this.#fetchingKurentoConfigPromise;
        this.#log(`#ensureKurentoRegistered(): id=${kurentoId} is not registered, fetching configuration`, 'warn');
        const configs = await this.#fetchKurentoConfig();

        let kurentoRegistered = false;
        for (let config of configs) {
            if (!this.#clients.has(config.id)) {
                this.registerKurento(config.id, config.wsUri, config.active);
                if (config.id === kurentoId) kurentoRegistered = true;
            }
        }
        if (!kurentoRegistered) {
            throw new Error(`Kurento id=${kurentoId} is not registered and config was not found`, {
                cause: `Kurento id=${kurentoId} is not registered and config was not found`,
            });
        }
    }

    async #fetchKurentoConfig() {
        try {
            const fetchConfig = axiosAiq('/admin/kurento/configuration/multiple');
            this.#fetchingKurentoConfigPromise = fetchConfig;
            const response = await fetchConfig;
            this.#fetchingKurentoConfigPromise = null;
            return response.data;
        } catch (err) {
            this.#log(`#fetchKurentoConfigById(): Failed to fetch kurento configs: ${err?.message}`, 'error');
            this.#fetchingKurentoConfigPromise = null;
            return [];
        }
    }

    #applyConfigToClient(clientObj, config) {
        clientObj.wsUri = config.wsUri || clientObj.wsUri;
        clientObj.active = config.active;
    }

    // Returns 'granted' | 'prompt' | 'denied' | 'unknown'. Reads the Permissions API; when that is
    // unavailable (Firefox lacks the 'microphone' name, or an insecure context has no mediaDevices),
    // falls back to the device-label heuristic - labels are populated only after access is granted.
    async #getMicPermissionState() {
        const state = await getPermissionState('microphone');
        if (state !== 'unknown') return state;

        if (!navigator.mediaDevices?.enumerateDevices) return 'unknown';
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            return devices.some((device) => device?.label) ? 'granted' : 'unknown';
        } catch (err) {
            this.#log(`#getMicPermissionState(): Error reading media devices: ${err?.message}`, 'error');
            return 'unknown';
        }
    }

    // Fire Chrome's native microphone prompt, non-blocking. Granting it lifts Chrome's mDNS local-IP
    // hiding (required for the WebRTC media path); the resolved stream is released immediately. Guarded
    // so an insecure context (mediaDevices undefined) never throws. Retries with video once when there
    // is no audio device - a camera grant lifts mDNS just as a mic grant does.
    #promptForMic(constraints = { audio: true }) {
        if (!navigator.mediaDevices?.getUserMedia) return;
        navigator.mediaDevices
            .getUserMedia(constraints)
            .then((stream) => stream.getTracks().forEach((track) => track.stop()))
            .catch((err) => {
                if (err?.name === 'NotFoundError' && constraints.audio) this.#promptForMic({ video: true });
                else this.#log(`#promptForMic(): getUserMedia rejected: ${err?.name || err}`, 'log');
            });
    }

    #log(message, mode = 'log') {
        if (!isCameraStreamLogsEnabled(mode)) return;
        console[mode](`[KurentoClientManager] ${message}`);
    }
}

export const kurentoClientManager = new KurentoClientManager();

if (import.meta.env.VITE_ENV !== 'production') window.kurentoClients = kurentoClientManager;
